import { useCallback, useContext } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { Auth0Context } from 'lane-shared/contexts/Auth0Context';
import routes from 'lane-shared/config/routes';

import { buildRedirectServiceReturnTo } from '../components/onboarding/Onboarding/useAuthClearSession';
import logout from '../helpers/logout';
import { useGetAuthToken } from './useGetAuthToken';

/**
 * Marks that we already bounced through `loginWithRedirect` to recover this
 * tab session. `loginWithRedirect` is a full-page navigation, so a failed
 * re-login can only be detected on the next load (the SDK errors again) —
 * this flag, persisted across that redirect, is how we know not to loop and
 * to escalate to a full logout instead.
 */
const RECOVERY_LOGIN_ATTEMPTED_KEY = 'auth0_recovery_login_attempted';

/**
 * True when the Auth0 SPA SDK has a cached session in local storage, i.e. the
 * user was logged in. Used to tell "session broke" apart from "never logged
 * in" so we don't bounce anonymous visitors through a recovery login.
 */
export function hasStoredAuth0Session(): boolean {
  return Object.keys(localStorage).some(key =>
    key.startsWith('@@auth0spajs@@')
  );
}

/**
 * Auth0 OAuth error codes that mean the session genuinely cannot be renewed:
 * the refresh token is missing, expired, or revoked, or interaction is
 * required. Only these warrant recovery (re-login / logout).
 *
 * Transient failures — a proxy/CORS 403, a network blip, a `getTokenSilently`
 * timeout — must NOT be mistaken for an unrenewable session. They surface as
 * other error codes (e.g. `timeout`) or as a bare fetch error with no `error`
 * field, and ejecting the user on them logs out an otherwise-valid session.
 */
const UNRENEWABLE_SESSION_ERROR_CODES = new Set([
  'login_required',
  'invalid_grant',
  'missing_refresh_token',
  'consent_required',
  'interaction_required',
]);

/**
 * True only when `error` is an Auth0 error whose code means the session is
 * genuinely unrenewable. Everything else (timeouts, network errors, the
 * redirect-service returning 403) is treated as transient by the caller.
 */
export function isUnrenewableSessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const errorCode = (error as { error?: unknown }).error;

  return (
    typeof errorCode === 'string' &&
    UNRENEWABLE_SESSION_ERROR_CODES.has(errorCode)
  );
}

/**
 * Recovers from an unrenewable Auth0 session without making the user manually
 * clear local storage, and without ever showing the shared (unbranded) Auth0
 * hosted login page:
 *
 * 1. First failure this tab session → a SILENT `loginWithRedirect`
 *    (`prompt: 'none'`). If the Auth0 SSO session is still alive this renews
 *    the dead refresh token with no UI at all. If it isn't, Auth0 bounces
 *    straight back with `login_required` — it never renders its hosted page,
 *    so the user never sees the wrong (cross-whitelabel) branding.
 * 2. If we already tried that and the session is still broken → full Auth0 +
 *    application logout, returning to the in-app `/identifier` route, which is
 *    whitelabel-branded, instead of leaving the user on the Auth0 host.
 *
 * The logout is inlined rather than reusing `useAuthClearSession` because this
 * hook runs in `Providers`, above the `<Router>`, where `useHistory` (pulled in
 * by `useAuthClearSession`) is unavailable.
 */
export function useAuth0SessionRecovery() {
  const { loginWithRedirect, logout: auth0Logout } = useAuth0();
  const auth0Context = useContext(Auth0Context);
  const getAuthToken = useGetAuthToken();

  const markSessionRecovered = useCallback(() => {
    sessionStorage.removeItem(RECOVERY_LOGIN_ATTEMPTED_KEY);
  }, []);

  const clearSession = useCallback(async () => {
    // Return to the in-app, whitelabel-branded identifier page rather than the
    // app root or the shared Auth0 host.
    const returnTo = buildRedirectServiceReturnTo(
      `${window.location.origin}${routes.identifier}`
    );

    auth0Context?.setIsAuth0LogoutInProgress(true);

    await logout(getAuthToken, {
      emitLogout: true,
      emitAuthTokenInvalid: true,
    });
    await auth0Logout({ openUrl: false });
    auth0Logout({ logoutParams: { returnTo } });
  }, [auth0Logout, getAuthToken, auth0Context]);

  const recoverSession = useCallback(async () => {
    if (!hasStoredAuth0Session()) {
      // No session to recover (anonymous visitor) — leave normal flow alone.
      return;
    }

    if (sessionStorage.getItem(RECOVERY_LOGIN_ATTEMPTED_KEY)) {
      sessionStorage.removeItem(RECOVERY_LOGIN_ATTEMPTED_KEY);
      await clearSession();

      return;
    }

    sessionStorage.setItem(RECOVERY_LOGIN_ATTEMPTED_KEY, 'true');

    try {
      // Silent renewal: renews via the live SSO session with no prompt, and
      // never renders the Auth0 hosted login page when the session is gone.
      await loginWithRedirect({ authorizationParams: { prompt: 'none' } });
    } catch {
      // loginWithRedirect threw before navigating away (e.g. SDK/network error
      // building the authorize URL). Fall straight through to a full logout +
      // clean login in this same load, so the user never sees a dead spinner
      // or has to reload / clear local storage themselves.
      sessionStorage.removeItem(RECOVERY_LOGIN_ATTEMPTED_KEY);
      await clearSession();
    }
  }, [loginWithRedirect, clearSession]);

  return { recoverSession, markSessionRecovered };
}
